import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  FileText,
  Plus,
  Loader2,
  Lock,
  Unlock,
  Share2,
  Trash2,
  Eye,
  Calendar,
  Stethoscope,
  Syringe,
  Pill,
  Heart,
  Shield,
  Users,
  Globe,
  AlertTriangle,
  CheckCircle,
  Clock,
  User,
  Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const recordTypes = [
  { value: 'vaccination', label: 'Vaccination', icon: Syringe },
  { value: 'vet_visit', label: 'Vet Visit', icon: Stethoscope },
  { value: 'lab_result', label: 'Lab Result', icon: FileText },
  { value: 'prescription', label: 'Prescription', icon: Pill },
  { value: 'surgery', label: 'Surgery', icon: Heart },
  { value: 'allergy', label: 'Allergy', icon: AlertTriangle },
  { value: 'condition', label: 'Condition', icon: Heart },
  { value: 'weight', label: 'Weight Record', icon: FileText },
  { value: 'dental', label: 'Dental', icon: Stethoscope },
  { value: 'xray', label: 'X-Ray', icon: FileText },
  { value: 'insurance', label: 'Insurance', icon: Shield },
  { value: 'other', label: 'Other', icon: FileText },
];

const privacyLevels = [
  { value: 'private', label: 'Private', icon: Lock, description: 'Only you can see' },
  { value: 'care_team', label: 'Care Team', icon: Users, description: 'You + your care team' },
  { value: 'selected', label: 'Selected Users', icon: Share2, description: 'Share with specific people' },
  { value: 'public', label: 'Public', icon: Globe, description: 'All members can see' },
];

const MedicalRecords = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [records, setRecords] = useState([]);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [myShares, setMyShares] = useState([]);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPet, setSelectedPet] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [saving, setSaving] = useState(false);

  const [newRecord, setNewRecord] = useState({
    pet_id: '',
    record_type: 'vet_visit',
    title: '',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    provider_name: '',
    provider_type: '',
    notes: '',
    privacy_level: 'private',
    is_sensitive: false
  });

  const [newShare, setNewShare] = useState({
    shared_with_user_id: '',
    expires_in_days: '',
    note: ''
  });

  useEffect(() => {
    if (user && token) {
      fetchData();
    }
  }, [user, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [recordsRes, sharedRes, sharesRes, petsRes] = await Promise.all([
        axios.get(`${API}/medical-records`, { headers }),
        axios.get(`${API}/medical-records/shares/shared-with-me`, { headers }),
        axios.get(`${API}/medical-records/shares/my`, { headers }),
        axios.get(`${API}/pets/my`, { headers })
      ]);

      setRecords(recordsRes.data);
      setSharedWithMe(sharedRes.data);
      setMyShares(sharesRes.data);
      setPets(petsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load medical records');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRecord = async () => {
    if (!newRecord.pet_id || !newRecord.title) {
      toast.error('Please select a pet and enter a title');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/medical-records`, newRecord, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Record created successfully');
      setCreateOpen(false);
      setNewRecord({
        pet_id: '',
        record_type: 'vet_visit',
        title: '',
        description: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        provider_name: '',
        provider_type: '',
        notes: '',
        privacy_level: 'private',
        is_sensitive: false
      });
      fetchData();
    } catch (error) {
      console.error('Error creating record:', error);
      toast.error(error.response?.data?.detail || 'Failed to create record');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      await axios.delete(`${API}/medical-records/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Record deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  const handleShare = async () => {
    if (!selectedRecord) return;

    setSaving(true);
    try {
      await axios.post(`${API}/medical-records/share`, {
        record_id: selectedRecord.id,
        ...newShare,
        expires_in_days: newShare.expires_in_days ? parseInt(newShare.expires_in_days) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Record shared successfully');
      setShareOpen(false);
      setNewShare({ shared_with_user_id: '', expires_in_days: '', note: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share record');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeShare = async (shareId) => {
    try {
      await axios.delete(`${API}/medical-records/shares/${shareId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Share revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke share');
    }
  };

  const filteredRecords = records.filter(r => {
    if (selectedPet !== 'all' && r.pet_id !== selectedPet) return false;
    if (selectedType !== 'all' && r.record_type !== selectedType) return false;
    return true;
  });

  const getRecordIcon = (type) => {
    const rt = recordTypes.find(r => r.value === type);
    return rt ? rt.icon : FileText;
  };

  const getPrivacyIcon = (level) => {
    const pl = privacyLevels.find(p => p.value === level);
    return pl ? pl.icon : Lock;
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <Lock className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
        <h2 className="text-2xl font-heading mb-4">Sign in Required</h2>
        <p className="text-muted-foreground mb-6">
          Please sign in to access your medical records.
        </p>
        <Button onClick={() => navigate('/auth')}>Sign In</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-background py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <FileText className="w-8 h-8 text-primary" />
              Medical Records
            </h1>
            <p className="text-muted-foreground">
              Securely store and manage your pet's health records
            </p>
          </div>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-record-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Record
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Medical Record</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Pet *</Label>
                  <Select value={newRecord.pet_id} onValueChange={(v) => setNewRecord({...newRecord, pet_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select pet" />
                    </SelectTrigger>
                    <SelectContent>
                      {pets.map(pet => (
                        <SelectItem key={pet.id} value={pet.id}>{pet.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Record Type</Label>
                  <Select value={newRecord.record_type} onValueChange={(v) => setNewRecord({...newRecord, record_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {recordTypes.map(rt => (
                        <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Title *</Label>
                  <Input
                    value={newRecord.title}
                    onChange={(e) => setNewRecord({...newRecord, title: e.target.value})}
                    placeholder="e.g., Annual vaccination"
                  />
                </div>

                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={newRecord.date}
                    onChange={(e) => setNewRecord({...newRecord, date: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Provider Name</Label>
                  <Input
                    value={newRecord.provider_name}
                    onChange={(e) => setNewRecord({...newRecord, provider_name: e.target.value})}
                    placeholder="e.g., Dr. Smith"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newRecord.description}
                    onChange={(e) => setNewRecord({...newRecord, description: e.target.value})}
                    placeholder="Details about the record..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={newRecord.notes}
                    onChange={(e) => setNewRecord({...newRecord, notes: e.target.value})}
                    placeholder="Private notes..."
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Privacy Level</Label>
                  <Select value={newRecord.privacy_level} onValueChange={(v) => setNewRecord({...newRecord, privacy_level: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {privacyLevels.map(pl => (
                        <SelectItem key={pl.value} value={pl.value}>
                          <div className="flex items-center gap-2">
                            <pl.icon className="w-4 h-4" />
                            {pl.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_sensitive"
                    checked={newRecord.is_sensitive}
                    onChange={(e) => setNewRecord({...newRecord, is_sensitive: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="is_sensitive" className="flex items-center gap-2 cursor-pointer">
                    <Shield className="w-4 h-4 text-amber-600" />
                    Encrypt sensitive content
                  </Label>
                </div>
                {newRecord.is_sensitive && (
                  <p className="text-xs text-muted-foreground bg-amber-50 p-2 rounded">
                    Description and notes will be encrypted. Only you can decrypt and view the full content.
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateRecord} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Create Record
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="my-records" className="space-y-6">
          <TabsList>
            <TabsTrigger value="my-records" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              My Records ({records.length})
            </TabsTrigger>
            <TabsTrigger value="shared-with-me" className="flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              Shared With Me ({sharedWithMe.length})
            </TabsTrigger>
            <TabsTrigger value="my-shares" className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              My Shares ({myShares.length})
            </TabsTrigger>
          </TabsList>

          {/* My Records Tab */}
          <TabsContent value="my-records">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <Select value={selectedPet} onValueChange={setSelectedPet}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by pet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Pets</SelectItem>
                  {pets.map(pet => (
                    <SelectItem key={pet.id} value={pet.id}>{pet.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {recordTypes.map(rt => (
                    <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredRecords.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <FileText className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Medical Records</h3>
                  <p className="text-muted-foreground mb-4">
                    Start tracking your pet's health by adding a record.
                  </p>
                  <Button onClick={() => setCreateOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Record
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredRecords.map(record => {
                  const RecordIcon = getRecordIcon(record.record_type);
                  const PrivacyIcon = getPrivacyIcon(record.privacy_level);
                  
                  return (
                    <Card key={record.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                              <RecordIcon className="w-6 h-6 text-primary" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium">{record.title}</h3>
                                {record.is_encrypted && (
                                  <Badge variant="outline" className="text-xs">
                                    <Lock className="w-3 h-3 mr-1" />
                                    Encrypted
                                  </Badge>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-2">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {record.date}
                                </span>
                                {record.pet_name && (
                                  <Badge variant="secondary">{record.pet_name}</Badge>
                                )}
                                <Badge variant="outline" className="capitalize">
                                  {record.record_type.replace('_', ' ')}
                                </Badge>
                                <span className="flex items-center gap-1">
                                  <PrivacyIcon className="w-3 h-3" />
                                  {record.privacy_level}
                                </span>
                              </div>
                              {record.description && (
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {record.description}
                                </p>
                              )}
                              {record.provider_name && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Provider: {record.provider_name}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedRecord(record);
                                setShareOpen(true);
                              }}
                              title="Share"
                            >
                              <Share2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteRecord(record.id)}
                              className="text-destructive hover:text-destructive"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Shared With Me Tab */}
          <TabsContent value="shared-with-me">
            {sharedWithMe.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Share2 className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Shared Records</h3>
                  <p className="text-muted-foreground">
                    Records shared with you by others will appear here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {sharedWithMe.map(record => {
                  const RecordIcon = getRecordIcon(record.record_type);
                  
                  return (
                    <Card key={record.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                            <RecordIcon className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-medium">{record.title}</h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Calendar className="w-3 h-3" />
                              {record.date}
                              {record.pet_name && (
                                <Badge variant="secondary">{record.pet_name}</Badge>
                              )}
                            </div>
                            {record.description && (
                              <p className="text-sm text-muted-foreground mt-2">
                                {record.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* My Shares Tab */}
          <TabsContent value="my-shares">
            {myShares.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <LinkIcon className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Active Shares</h3>
                  <p className="text-muted-foreground">
                    Records you've shared with others will appear here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {myShares.map(share => (
                  <Card key={share.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">{share.record_title || 'Record'}</h3>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            {share.shared_with_username ? (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                Shared with @{share.shared_with_username}
                              </span>
                            ) : share.share_token ? (
                              <span className="flex items-center gap-1">
                                <LinkIcon className="w-3 h-3" />
                                Share link created
                              </span>
                            ) : null}
                            {share.expires_at && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Expires: {new Date(share.expires_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeShare(share.id)}
                          className="text-destructive"
                        >
                          Revoke
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Share Dialog */}
        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share Medical Record</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedRecord && (
                <p className="text-sm text-muted-foreground">
                  Sharing: <strong>{selectedRecord.title}</strong>
                </p>
              )}
              
              <div>
                <Label>Share with User ID (optional)</Label>
                <Input
                  value={newShare.shared_with_user_id}
                  onChange={(e) => setNewShare({...newShare, shared_with_user_id: e.target.value})}
                  placeholder="Enter user ID to share directly"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty to generate a share link instead
                </p>
              </div>

              <div>
                <Label>Expires in (days)</Label>
                <Input
                  type="number"
                  value={newShare.expires_in_days}
                  onChange={(e) => setNewShare({...newShare, expires_in_days: e.target.value})}
                  placeholder="Leave empty for no expiration"
                  min="1"
                  max="365"
                />
              </div>

              <div>
                <Label>Note (optional)</Label>
                <Textarea
                  value={newShare.note}
                  onChange={(e) => setNewShare({...newShare, note: e.target.value})}
                  placeholder="Add a note for the recipient"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShareOpen(false)}>Cancel</Button>
              <Button onClick={handleShare} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Share Record
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MedicalRecords;
